import {
  getAttributes,
  getCategory,
  getPsetsAndQsets,
  formatValue,
  type KeyValue,
  type PsetGroup,
} from '@viewer/lib/ifcItemData'
import type { ConjuntosDoElemento } from '@viewer/lib/psetsHerdados'

// ---------------------------------------------------------------------------
// Comparação de propriedades entre 2+ elementos: matriz com uma coluna por
// elemento (linhas = atributos / propriedades / quantidades). Núcleo PURO
// sobre os itens já lidos (getItemsData com relations), testável sem modelo.
//
// Uma linha é "igual" (allEqual) quando TODOS os elementos têm a chave com
// o mesmo valor; caso contrário é realçada. Chaves presentes só nalguns
// elementos aparecem com "-" nos que não a têm (também conta como
// diferença).
// ---------------------------------------------------------------------------

/** Nº máximo de elementos comparados lado a lado (colunas). Acima disto a
 *  tabela fica ilegível; comparam-se os primeiros e sinaliza-se. */
export const MAX_COMPARE_COLUMNS = 8

export interface CompareRow {
  key: string
  ifcType?: string
  /** Valor formatado por elemento (alinhado com labels/colunas);
   *  undefined quando o elemento não tem esta chave. */
  values: (string | undefined)[]
  /** true quando todos os elementos têm a chave com o mesmo valor. */
  allEqual: boolean
}
export interface CompareGroup {
  name: string
  ifcClass: string
  rows: CompareRow[]
}
export interface CompareResult {
  /** Rótulo de cada coluna (elemento). */
  labels: string[]
  /** O modelo de origem de cada coluna (nome curto), quando o chamador
   *  o dá: com vários modelos federados, a coluna diz de onde vem. */
  modelos: (string | null)[]
  attributes: CompareRow[]
  psets: CompareGroup[]
  qsets: CompareGroup[]
  /** Categoria comum a todos, quando existe (dica de identidade). */
  commonCategory: string | null
}

function labelFor(item: Record<string, unknown>, index: number): string {
  const name = formatValue(item.Name)
  if (name) return name
  const cat = getCategory(item)
  return cat ? `${cat} ${index + 1}` : `#${index + 1}`
}

/** A unidade de uma linha de um elemento (pelo índice do elemento), em
 *  texto ("mm (#11)"), ou null se o valor não leva unidade. */
export type UnidadeNaComparacao = (indice: number, linha: KeyValue) => string | null

/** O valor como se mostra na matriz: com a unidade (e a entidade de onde
 *  veio) e a indicação de que vem do type. Dois valores só são iguais se
 *  o forem também na unidade: "8 m (#90)" não é "8000 mm (#11)". */
function valorMostrado(kv: KeyValue, indice: number, unidade?: UnidadeNaComparacao): string {
  const u = unidade?.(indice, kv) ?? null
  let v = kv.value
  if (u) v = u.startsWith('unidade') ? `${v} (${u})` : `${v} ${u}`
  return kv.origem === 'type' ? `${v} (do type)` : v
}

/** Constrói as linhas de comparação a partir de um mapa chave->KeyValue
 *  por elemento. Diferenças primeiro, depois por chave. */
function buildRows(valueMaps: Map<string, KeyValue>[], unidade?: UnidadeNaComparacao): CompareRow[] {
  const allKeys = new Set<string>()
  for (const m of valueMaps) for (const k of m.keys()) allKeys.add(k)
  const rows: CompareRow[] = []
  for (const key of allKeys) {
    const kvs = valueMaps.map((m) => m.get(key))
    const values = kvs.map((kv, i) => (kv ? valorMostrado(kv, i, unidade) : undefined))
    const first = kvs.find((kv): kv is KeyValue => kv !== undefined)
    const allPresent = kvs.every((kv) => kv !== undefined)
    const allEqual = allPresent && values.every((v) => v === values[0])
    rows.push({ key, ifcType: first?.ifcType, values, allEqual })
  }
  rows.sort((a, b) => {
    if (a.allEqual !== b.allEqual) return a.allEqual ? 1 : -1
    return a.key.localeCompare(b.key)
  })
  return rows
}

/** Une os sets (por nome) de todos os elementos e compara as suas
 *  propriedades. Sets presentes só nalguns elementos aparecem com "-"
 *  nos restantes. */
function buildGroups(groupsPerItem: PsetGroup[][], unidade?: UnidadeNaComparacao): CompareGroup[] {
  const maps = groupsPerItem.map((groups) => {
    const m = new Map<string, PsetGroup>()
    for (const g of groups) m.set(g.name, g)
    return m
  })
  const allNames = new Set<string>()
  for (const m of maps) for (const k of m.keys()) allNames.add(k)
  const out: CompareGroup[] = []
  for (const name of allNames) {
    const perItem = maps.map((m) => m.get(name))
    const rowMaps = perItem.map((g) => {
      const m = new Map<string, KeyValue>()
      if (g) for (const r of g.rows) m.set(r.key, r)
      return m
    })
    const rows = buildRows(rowMaps, unidade)
    if (rows.length === 0) continue
    const ifcClass =
      perItem.find((g) => g !== undefined)?.ifcClass ?? 'IFCPROPERTYSET'
    out.push({ name, ifcClass, rows })
  }
  out.sort((a, b) => a.name.localeCompare(b.name))
  return out
}

/** Compara os itens (já limitados a MAX_COMPARE_COLUMNS pelo chamador).
 *  Núcleo puro. */
export function computeCompare(
  items: Record<string, unknown>[],
  opcoes: {
    /** Os conjuntos de cada elemento já juntos com os do type (ver
     *  psetsHerdados.ts). Sem isto, só os da instância. */
    conjuntos?: ConjuntosDoElemento[]
    unidade?: UnidadeNaComparacao
    /** O nome curto do modelo de cada elemento (alinhado com items). */
    modelos?: (string | null)[]
  } = {},
): CompareResult {
  if (items.length === 0) {
    return {
      labels: [],
      modelos: [],
      attributes: [],
      psets: [],
      qsets: [],
      commonCategory: null,
    }
  }
  const labels = items.map(labelFor)
  const attrMaps = items.map((it) => {
    const m = new Map<string, KeyValue>()
    for (const r of getAttributes(it)) m.set(r.key, r)
    return m
  })
  const attributes = buildRows(attrMaps)
  const splits = items.map((it, i) => opcoes.conjuntos?.[i] ?? getPsetsAndQsets(it))
  const psets = buildGroups(splits.map((s) => s.psets), opcoes.unidade)
  const qsets = buildGroups(splits.map((s) => s.qsets), opcoes.unidade)
  const cats = items.map(getCategory).filter(Boolean)
  const commonCategory =
    cats.length === items.length && cats.every((c) => c === cats[0])
      ? cats[0]
      : null
  const modelos = items.map((_, i) => opcoes.modelos?.[i] ?? null)
  return { labels, modelos, attributes, psets, qsets, commonCategory }
}
